// PRD §7.4.4 — Mentor's view of their own proposed missions (slice B).
// Shows every proposal the mentor has submitted, with the current Viren and
// founder gate states. Distinct from /mentor/interests (which lists EXPRESS-
// INTEREST rows on existing missions) — this is "missions I have proposed".
import { useEffect, useState } from "react";
import ProgressLoader from "@/components/progress-loader";

const BASE = import.meta.env.BASE_URL;

interface Proposal {
  id: number;
  companyId: number;
  companyName: string;
  mentorTitle: string;
  virenStatus: "pending_refinement" | "refined" | "rejected_by_viren" | "refinement_failed";
  founderStatus: "awaiting" | "accepted" | "declined" | "blocked";
  virenRefinedTitle: string | null;
  virenRationale: string | null;
  virenRejectionReason: string | null;
  declineReason: string | null;
  convertedMissionId: number | null;
  createdAt: string;
  founderRespondedAt: string | null;
}

export default function MentorProposals() {
  const [rows, setRows] = useState<Proposal[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const tok = localStorage.getItem("mentor_token") || "";
    fetch(`${BASE}api/mentor-portal/my-proposed-missions`, { headers: { "x-mentor-token": tok } })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.error || `${r.status}`)))
      .then(d => setRows(d.proposals))
      .catch(e => setErr(String(e)));
  }, []);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-10 text-sm text-red-400" data-testid="text-proposals-error">{err}</div>;
  if (!rows) return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <ProgressLoader active={true} title="Loading" stages={["Loading your proposals", "Cross-referencing companies"]} />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-bold mb-2">My proposed missions</h1>
      <p className="text-sm text-[var(--mentor-fg-muted)] mb-6">Missions you've proposed. Viren reviews each one for fit before the founder sees it.</p>

      {rows.length === 0 ? (
        <div className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-8 text-center text-sm text-[var(--mentor-fg-muted)]" data-testid="text-no-proposals-empty">
          You haven't proposed any missions yet. Open a company and use "Propose a mission for this company".
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-4" data-testid={`proposal-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{r.virenRefinedTitle || r.mentorTitle}</div>
                  <div className="text-xs text-[var(--mentor-fg-muted)] mt-0.5">
                    {r.companyName} · proposed {fmt(r.createdAt)}
                  </div>
                  {r.virenStatus === "rejected_by_viren" && r.virenRejectionReason && (
                    <div className="mt-2 text-xs text-red-300">
                      <span className="font-semibold">Viren held this back:</span> {r.virenRejectionReason}
                    </div>
                  )}
                  {r.founderStatus === "declined" && r.declineReason && (
                    <div className="mt-2 text-xs text-red-300">
                      <span className="font-semibold">Founder note:</span> {r.declineReason}
                    </div>
                  )}
                  {r.founderStatus === "accepted" && r.convertedMissionId && (
                    <div className="mt-2 text-xs text-emerald-300">
                      Accepted — now live as mission #{r.convertedMissionId}. Your interest is auto-attached.
                    </div>
                  )}
                </div>
                <Badge proposal={r} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Badge({ proposal: r }: { proposal: Proposal }) {
  if (r.virenStatus === "rejected_by_viren") return <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-red-500/15 text-red-300">Viren rejected</span>;
  if (r.virenStatus === "refinement_failed") return <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-zinc-500/15 text-zinc-300">Refinement failed</span>;
  if (r.virenStatus === "pending_refinement") return <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-zinc-500/15 text-zinc-300">Viren reviewing</span>;
  // virenStatus === 'refined'
  if (r.founderStatus === "awaiting") return <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-amber-500/15 text-amber-300">Awaiting founder</span>;
  if (r.founderStatus === "accepted") return <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300">Accepted</span>;
  if (r.founderStatus === "declined") return <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-red-500/15 text-red-300">Declined</span>;
  return <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-zinc-500/15 text-zinc-300">{r.founderStatus}</span>;
}

function fmt(d: string | null) { if (!d) return ""; return new Date(d).toLocaleDateString(); }
